import * as Sentry from '@sentry/react'
import React from 'react'
import ReactDOM from 'react-dom/client'
import AppBootstrap from './components/AppBootstrap'
import ErrorFallback from './components/ErrorFallback'
import LandingGate from './components/LandingGate'
import AuthErrorPage from './pages/AuthErrorPage'
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'
import WhyPage from './pages/WhyPage'
import { beforeSend, ignoreErrors } from './lib/sentryFilters'
import { hasReturningVisitorFlag } from './lib/returningVisitor'
import './index.css'

const _sentryDsn = import.meta.env.VITE_SENTRY_DSN
const _apiBase = import.meta.env.VITE_API_URL ?? ''
if (_sentryDsn) {
  Sentry.init({
    dsn: _sentryDsn,
    environment: import.meta.env.MODE,
    // Git SHA injected at build time (see vite.config.ts); ties errors to the
    // exact commit and matches the source maps uploaded for that release.
    release: import.meta.env.VITE_RELEASE,
    // Events are proxied through the backend to survive ad-blockers. That means
    // pre-gate errors are undeliverable while the backend is down — an accepted,
    // documented gap. See docs/decisions/2026-08-04-landing-pregate-hardening.md.
    tunnel: `${_apiBase}/sentry-tunnel`,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    ignoreErrors,
    beforeSend,
  })
}

// Lazy-loaded chunks (e.g. SuperDocEditor) are hashed filenames baked into the
// index.html a tab already has loaded. After a new deploy those hashes no
// longer exist on the server, so the dynamic import fails with
// vite:preloadError. Reload once to pick up the current index.html/hashes;
// the sessionStorage guard stops a reload loop if the build is genuinely broken.
window.addEventListener('vite:preloadError', () => {
  const key = 'pow_chunk_reload_once'
  if (sessionStorage.getItem(key)) return
  sessionStorage.setItem(key, '1')
  window.location.reload()
})

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element not found')

const root = ReactDOM.createRoot(rootElement)

/**
 * Routes that render with zero backend dependency, outside the warm-up gate.
 *
 * `/` is the public landing page. `/privacy` and `/terms` are static legal
 * content with no backend dependency either — and they are the links a stranger
 * clicks to decide whether to trust the page, so putting them behind the gate
 * meant a cold backend showed them WarmingUpScreen for ~13s.
 * See docs/decisions/2026-08-04-pow-returning-flag.md. `/why` is the "why I
 * built this" page, static for the same reason as the legal pages.
 * `/auth/error` is static too, and it is where sign-in lands when the service
 * is unavailable — gating it would hold the explanation behind the very outage
 * it explains (docs/decisions/2026-10-19-supabase-pause-handling.md).
 */
const PREGATE_PATHS: string[] = ['/', '/privacy', '/terms', '/why', '/auth/error']

const path = window.location.pathname
// The legal and /why pages are static and always render pre-gate. `/`
// additionally requires the returning-visitor hint to be absent — with the
// hint present it goes through the normal gated app instead.
const showPregate = PREGATE_PATHS.includes(path) && (path !== '/' || !hasReturningVisitorFlag())

// LandingGate additionally runs a silent authenticated-session probe. That is
// only meaningful at `/`, so it never runs on the legal or /why pages.
const pregateElement =
  path === '/privacy' ? (
    <PrivacyPage />
  ) : path === '/terms' ? (
    <TermsPage />
  ) : path === '/why' ? (
    <WhyPage />
  ) : path === '/auth/error' ? (
    <AuthErrorPage />
  ) : (
    <LandingGate />
  )

root.render(
  <React.StrictMode>
    {showPregate ? (
      <Sentry.ErrorBoundary fallback={<ErrorFallback />}>{pregateElement}</Sentry.ErrorBoundary>
    ) : (
      <AppBootstrap />
    )}
  </React.StrictMode>,
)
